import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { FiDollarSign, FiRefreshCw, FiSettings, FiExternalLink } from 'react-icons/fi';
import api from '../../config/api';
import IconButton from '../IconButton';
import { formatLocalDateTime } from '../../utils/formatTimestamp';
import ListPager from '../ListPager';
import { useClientPager } from '../../hooks/useClientPager';

function formatRate(rate) {
  if (rate == null || rate === '') return '—';
  const n = Number(rate);
  if (!Number.isFinite(n)) return String(rate);
  if (n >= 1000) return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
  if (n >= 1) return n.toLocaleString(undefined, { maximumFractionDigits: 4 });
  return n.toLocaleString(undefined, { maximumFractionDigits: 6 });
}

const CurrencyMetadataPanel = ({ type }) => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const Icon = type.icon || FiDollarSign;

  const { data: rows = [], isLoading, isFetching, refetch, error } = useQuery(
    ['currencies', 'metadata'],
    () => api.get('/currencies', { params: { include_inactive: 'true' } }).then((r) => r.data),
    { staleTime: 15_000 }
  );

  const { data: settings } = useQuery(
    ['currencies-settings'],
    () => api.get('/currencies/settings').then((r) => r.data),
    { staleTime: 30_000 }
  );

  const filtered = rows.filter((row) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return [row.code, row.name, row.country, row.country_code, row.symbol]
      .filter(Boolean)
      .some((v) => String(v).toLowerCase().includes(q));
  });
  const {
    pageItems: pagedCurrencies,
    pagination: currencyPagination,
    setPage: setCurrencyPage,
  } = useClientPager(filtered, { resetKey: search });

  return (
    <section className="metadata-section" aria-labelledby="metadata-section-currencies">
      <div className="metadata-section-head">
        <div className="metadata-section-head-text">
          <h2 id="metadata-section-currencies">
            <Icon aria-hidden />
            {type.label}
          </h2>
          <p className="section-description">{type.description}</p>
        </div>
        <div className="metadata-section-actions">
          <IconButton
            icon={FiRefreshCw}
            label="Refresh currencies"
            loading={isFetching}
            onClick={() => refetch()}
          />
          <IconButton
            icon={FiSettings}
            label="Currency sync settings"
            onClick={() => navigate('/settings?tab=currency')}
          />
        </div>
      </div>

      <div className="metadata-meta-grid">
        <div className="metadata-meta-card">
          <span>Base</span>
          <strong>{settings?.base_currency || '—'}</strong>
        </div>
        <div className="metadata-meta-card">
          <span>Provider</span>
          <strong>{settings?.provider_label || settings?.provider || '—'}</strong>
        </div>
        <div className="metadata-meta-card">
          <span>Last sync</span>
          <strong>
            {settings?.last_synced_at
              ? formatLocalDateTime(settings.last_synced_at)
              : 'Never'}
          </strong>
        </div>
        <div className="metadata-meta-card">
          <span>Status</span>
          <strong className={settings?.last_sync_status === 'error' ? 'is-error' : ''}>
            {settings?.last_sync_status || '—'}
            {settings?.last_sync_count != null && settings.last_sync_status === 'ok'
              ? ` · ${settings.last_sync_count} rows`
              : ''}
          </strong>
        </div>
      </div>

      {settings?.last_sync_error ? (
        <p className="metadata-inline-error">{settings.last_sync_error}</p>
      ) : null}

      <p className="metadata-inline-note">
        Rates are pulled from the configured provider and overwrite this table. Configure provider and
        base currency, then Sync now in{' '}
        <Link to="/settings?tab=currency">
          Settings <FiExternalLink aria-hidden />
        </Link>
        .
      </p>

      <div className="metadata-toolbar">
        <input
          type="search"
          placeholder="Filter by code, name, country…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Filter currencies"
        />
        <span className="metadata-toolbar-count">
          {filtered.length} of {rows.length}
        </span>
      </div>

      {isLoading && !rows.length ? (
        <div className="loading">Loading currencies…</div>
      ) : error ? (
        <div className="empty-state">
          <p>Failed to load currencies: {error.response?.data?.error || error.message}</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <p>
            No currency rows yet. Open{' '}
            <Link to="/settings?tab=currency">Settings → Currency</Link> and run Sync now.
          </p>
        </div>
      ) : (
        <div className="metadata-table-container">
          <table className="metadata-table currency-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Currency</th>
                <th>Country</th>
                <th>Symbol</th>
                <th>Rate (per 1 {settings?.base_currency || 'base'})</th>
                <th>As of</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              {pagedCurrencies.map((row) => (
                <tr key={row.code} className={`metadata-row${row.is_base ? ' is-base' : ''}`}>
                  <td>
                    <strong>{row.code}</strong>
                    {row.is_base ? <span className="currency-base-tag">base</span> : null}
                  </td>
                  <td>{row.name}</td>
                  <td>
                    {row.country || '—'}
                    {row.country_code ? <span className="currency-cc">{row.country_code}</span> : null}
                  </td>
                  <td>{row.symbol || '—'}</td>
                  <td className="currency-rate">{formatRate(row.conversion_rate)}</td>
                  <td>{row.rate_as_of ? String(row.rate_as_of).slice(0, 10) : '—'}</td>
                  <td>{row.source || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ListPager
        page={currencyPagination.page}
        pageCount={currencyPagination.pageCount}
        total={currencyPagination.total}
        limit={currencyPagination.limit}
        onPageChange={setCurrencyPage}
      />
    </section>
  );
};

export default CurrencyMetadataPanel;
